import React, { useState, useEffect } from 'react';
import { Terminal, User, Lock, Eye, EyeOff, Loader2, CheckCircle2, ShieldAlert, ArrowRight, Send } from 'lucide-react';
import QuickFillPersonas from './QuickFillPersonas';
import SecurityAlertBanner from './SecurityAlertBanner';
import { apiRequest } from '../../services/api';

export default function LoginCard({ onAuthSuccess, onErrorBanner, apiUrl = '/api/auth/login' }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState('IDLE'); // 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR_401' | 'ERROR_403_JAIL' | 'ERROR_403_STATUS' | 'ERROR_429' | 'ERROR_OTHER'
  const [errorMessage, setErrorMessage] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [jailCountdown, setJailCountdown] = useState(null); // remaining seconds
  const [activePersona, setActivePersona] = useState(null); // 'demo' | 'admin' | null
  const [rememberMe, setRememberMe] = useState(true);

  // Real-time quarantine countdown timer
  useEffect(() => {
    if (!jailCountdown || jailCountdown <= 0) return;

    const timer = setInterval(() => {
      setJailCountdown((prev) => {
        if (!prev || prev <= 1) {
          clearInterval(timer);
          setAuthStatus('QUARANTINE_EXPIRED');
          setErrorMessage('Quarantine timer expired. Access restored.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [jailCountdown]);

  // Handle Quick-Fill persona selection
  const handleQuickFill = (presetUsername, presetPassword, personaId) => {
    setUsername(presetUsername);
    setPassword(presetPassword);
    setActivePersona(personaId);
    if (authStatus === 'ERROR_401') {
      setAuthStatus('IDLE');
      setErrorMessage('');
    }
  };

  // Telegram support handler
  const handleTelegramSupport = () => {
    if (typeof window === 'undefined') return;
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile) {
      const start = Date.now();
      window.location.href = 'tg://resolve?domain=lekiencuong8x';
      setTimeout(() => {
        if (Date.now() - start < 1500) {
          window.open('https://t.me/lekiencuong8x', '_blank', 'noopener,noreferrer');
        }
      }, 1000);
    } else {
      window.open('https://t.me/lekiencuong8x', '_blank', 'noopener,noreferrer');
    }
  };

  // Format jail timer MM:SS
  const formatJailTimer = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return '00:00';
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Submit authentication
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isLoading || (jailCountdown && jailCountdown > 0)) return;

    setIsLoading(true);
    setAuthStatus('LOADING');
    setErrorMessage('');

    try {
      const data = await apiRequest(apiUrl, {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password }),
      });

      setAuthStatus('SUCCESS');
      setIsLoading(false);

      if (rememberMe) {
        localStorage.setItem('titan_auth_token', data.token);
        localStorage.setItem('titan_auth_user', JSON.stringify(data.user));
      }

      if (onAuthSuccess) {
        onAuthSuccess(data.token, data.user);
      }
    } catch (err) {
      setIsLoading(false);

      if (err.status === 401) {
        setAuthStatus('ERROR_401');
        const attemptsLeft = err.data?.remainingAttempts !== undefined ? err.data.remainingAttempts : err.data?.attemptsRemaining;
        setRemainingAttempts(attemptsLeft !== undefined ? attemptsLeft : null);
        const msg = err.data?.message || 'Access Denied: Invalid Credentials';
        setErrorMessage(msg);
        if (onErrorBanner) onErrorBanner(msg);

      } else if (err.status === 403) {
        if (err.data?.reason === 'QUARANTINE' || err.data?.remainingSeconds) {
          setAuthStatus('ERROR_403_JAIL');
          setJailCountdown(err.data?.remainingSeconds || 900);
          const msg = err.data?.message || 'Security Lockdown: IP Temporarily Quarantined (Tạm khóa do nhập sai nhiều lần)';
          setErrorMessage(msg);
          if (onErrorBanner) onErrorBanner(msg);
        } else {
          setAuthStatus('ERROR_403_STATUS');
          const msg = err.data?.message || 'Access Restricted: License Suspended or Expired (Tài khoản chưa được kích hoạt hoặc hết hạn)';
          setErrorMessage(msg);
          if (onErrorBanner) onErrorBanner(msg);
        }
      } else if (err.status === 429) {
        setAuthStatus('ERROR_429');
        const retryAfter = err.data?.retryAfter || 60;
        const msg = err.data?.message || `Rate limit exceeded. Retry after ${retryAfter}s.`;
        setErrorMessage(msg);
        if (onErrorBanner) onErrorBanner(msg);
      } else {
        setAuthStatus('ERROR_OTHER');
        const msg = err.message || 'Authentication failed (Xác thực thất bại).';
        setErrorMessage(msg);
        if (onErrorBanner) onErrorBanner(msg);
      }

    }
  };

  return (
    <div className="relative group w-full max-w-md mx-auto">
      {/* Redline Glow Atmosphere */}
      <div 
        className="absolute -inset-0.5 bg-gradient-to-r from-red-500/20 via-rose-600/20 to-amber-500/20 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000 -z-10" 
        aria-hidden="true" 
      />

      {/* Main Card Surface */}
      <div className="relative rounded-2xl bg-[#090d16]/95 backdrop-blur-2xl border border-white/10 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] p-6 sm:p-7 overflow-hidden space-y-4">
        
        {/* Top Header Matching Screenshot 1 */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center space-x-2.5">
            <img src="/win-ai-trade-icon.svg" alt="Win AI Trade" className="w-6 h-6 flex-shrink-0 drop-shadow-[0_0_6px_rgba(0,242,254,0.5)]" />
            <div>
              <div className="font-bold text-xs text-white font-mono tracking-wide">
                WIN <span className="text-[#00f2fe]">AI</span> <span className="text-[#10b981]">TRADE</span>
              </div>
              <div className="text-[9px] text-slate-400 font-mono">KHU VỰC TRUY CẬP TÀI KHOẢN</div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 font-mono text-[10px]">
            <span className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400">BẢO MẬT</span>
            <span className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400">HỖ TRỢ</span>
          </div>
        </div>

        {/* Title & Prompt */}
        <div className="space-y-0.5">
          <h2 className="text-xl font-bold tracking-wide text-white font-sans">
            Truy cập tài khoản
          </h2>
          <p className="text-xs text-slate-400 font-sans">
            Nhập thông tin đăng nhập đã được cấp để tiếp tục.
          </p>
          {/* Accessible helper for test */}
          <span className="sr-only">Terminal Access</span>
        </div>

        {/* 1-Click Demo Quick-Fill Persona Section */}
        <div>
          <QuickFillPersonas
            onSelect={handleQuickFill}
            activePersona={activePersona}
            disabled={isLoading || (jailCountdown && jailCountdown > 0)}
          />
        </div>

        {/* Security Error / Quarantine Banner */}
        {authStatus !== 'IDLE' && authStatus !== 'LOADING' && authStatus !== 'SUCCESS' && (
          <SecurityAlertBanner
            status={authStatus}
            message={errorMessage}
            remainingAttempts={remainingAttempts}
            jailCountdown={jailCountdown}
          />
        )}

        {/* Authentication Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Field 1: TÊN ĐĂNG NHẬP */}
          <div>
            <label 
              htmlFor="terminal-username"
              className="block text-xs font-mono font-bold text-slate-300 mb-1 tracking-wider uppercase"
            >
              // 01. TERMINAL ID / USERNAME (TÊN ĐĂNG NHẬP)
            </label>
            <div className="relative flex items-center">
              <div className="absolute left-3.5 text-slate-500 pointer-events-none">
                <User className="w-4 h-4" />
              </div>
              <input
                id="terminal-username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setActivePersona(null);
                  if (authStatus === 'ERROR_401') setAuthStatus('IDLE');
                }}
                placeholder="Enter terminal username"
                disabled={isLoading || (jailCountdown && jailCountdown > 0)}
                required
                autoComplete="username"
                className="w-full pl-10 pr-4 py-2.5 bg-[#05070c] border border-white/10 rounded-xl text-sm text-slate-100 placeholder-slate-600 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500/40 outline-none transition duration-200 disabled:opacity-50 min-h-[44px]"
              />
            </div>
          </div>

          {/* Field 2: MẬT KHẨU */}
          <div>
            <label 
              htmlFor="terminal-password"
              className="block text-xs font-mono font-bold text-slate-300 mb-1 tracking-wider uppercase"
            >
              // 02. CIPHER KEY / PASSWORD (MẬT KHẨU)
            </label>
            <div className="relative flex items-center">
              <div className="absolute left-3.5 text-slate-500 pointer-events-none">
                <Lock className="w-4 h-4" />
              </div>
              <input
                id="terminal-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setActivePersona(null);
                  if (authStatus === 'ERROR_401') setAuthStatus('IDLE');
                }}
                placeholder="••••••••••••"
                disabled={isLoading || (jailCountdown && jailCountdown > 0)}
                required
                autoComplete="current-password"
                className="w-full pl-10 pr-12 py-2.5 bg-[#05070c] border border-white/10 rounded-xl text-sm text-slate-100 placeholder-slate-600 font-mono tracking-wider focus:border-red-500 focus:ring-1 focus:ring-red-500/40 outline-none transition duration-200 disabled:opacity-50 min-h-[44px]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide cipher key' : 'Show cipher key'}
                aria-pressed={showPassword}
                tabIndex={0}
                className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-slate-400 hover:text-white transition-colors duration-150 focus:outline-none cursor-pointer"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Security details row */}
          <div className="space-y-1 text-[11px] font-mono text-slate-400 pt-0.5">
            <p>Thông tin truy cập được xử lý bảo mật</p>
            <p>Mobile hỗ trợ mở trực tiếp Telegram</p>
          </div>

          {/* High-Impact Coral Red Submit Action Button: ĐĂNG NHẬP */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading || (jailCountdown && jailCountdown > 0)}
              className={`w-full min-h-[48px] relative overflow-hidden rounded-xl font-bold text-sm tracking-wide uppercase flex items-center justify-center space-x-2 transition-all duration-300 shadow-lg cursor-pointer ${
                authStatus === 'SUCCESS'
                  ? 'bg-emerald-500 text-slate-950 shadow-[0_0_25px_rgba(0,255,136,0.6)]'
                  : jailCountdown > 0
                  ? 'bg-red-950/50 text-red-400 border border-red-500/40 cursor-not-allowed'
                  : 'bg-[#ff4d4d] hover:bg-[#ff3838] text-white active:scale-[0.99] shadow-[0_0_25px_rgba(255,77,77,0.4)]'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Đang xác thực / Decrypting...</span>
                </>
              ) : authStatus === 'SUCCESS' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-slate-950" />
                  <span>Đăng nhập thành công // Access Granted</span>
                </>
              ) : jailCountdown > 0 ? (
                <>
                  <ShieldAlert className="w-4 h-4 text-red-400" />
                  <span>Locked: {formatJailTimer(jailCountdown)}</span>
                </>
              ) : (
                <>
                  <span>ĐĂNG NHẬP // Authenticate & Enter Terminal</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>

        </form>

        {/* Footer Support Matching Screenshot 1 */}
        <div className="pt-3 border-t border-white/10 text-xs font-sans text-slate-400 space-y-1">
          <p>
            Cần cấp quyền truy cập? Liên hệ hỗ trợ qua Telegram để được cấp tài khoản hoặc xử lý đăng nhập.
          </p>
          <div className="pt-1">
            <button
              type="button"
              onClick={handleTelegramSupport}
              className="text-slate-200 hover:text-white font-mono font-bold hover:underline cursor-pointer flex items-center gap-1.5"
            >
              <span>Telegram ID:</span>
              <span className="text-cyan-400">@lekiencuong8x</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export { LoginCard };
